import type { CSSProperties } from 'react';
import type { TargetAndTransition } from 'framer-motion';
import { ColorType, getColorValue } from '@/theme/colors';

interface HeroRootStyleInput {
  viewportHeightUnit: number;
  style?: CSSProperties;
}

interface ViewportStyleInput {
  isMobile: boolean;
}

interface HeroLogoAnimationInput {
  isLogoReady: boolean;
}

interface HeroControlsAnimationInput {
  isControlsVisible: boolean;
}

interface HeroControlsWrapperStyleInput extends ViewportStyleInput {
  isControlsVisible: boolean;
}

interface HeroTextMetrics {
  logoNameSize: number;
  logoSuffixSize: number;
  taglineSize: number;
}

export function getHeroRootStyle({
  viewportHeightUnit,
  style,
}: HeroRootStyleInput): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: viewportHeightUnit * 100,
    minHeight: viewportHeightUnit * 100,
    maxHeight: viewportHeightUnit * 100,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
    position: 'relative',
    backgroundColor: getColorValue(ColorType.Black),
    scrollSnapAlign: 'start',
    scrollSnapStop: 'always',
    overflow: 'hidden',
    ...style,
  };
}

export function getHeroContentWrapperStyle({
  viewportHeightUnit,
}: HeroRootStyleInput): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 0,
    position: 'relative',
    zIndex: 1,
    width: '100%',
    boxSizing: 'border-box',
    padding: `${viewportHeightUnit * 13}px 0px`,
    height: viewportHeightUnit * 100,
  };
}

export function getHeroTextContainerStyle(): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 0,
  };
}

export function getHeroLogoContainerStyle(): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'baseline',
    gap: 15,
  };
}

export function getHeroTextMetrics({
  isMobile,
}: ViewportStyleInput): HeroTextMetrics {
  if (isMobile) {
    return {
      logoNameSize: 48,
      logoSuffixSize: 32,
      taglineSize: 32,
    };
  }

  return {
    logoNameSize: 70,
    logoSuffixSize: 50,
    taglineSize: 50,
  };
}

export function getHeroLogoAnimation({
  isLogoReady,
}: HeroLogoAnimationInput): TargetAndTransition {
  if (isLogoReady) {
    return { opacity: 1, scale: 1 };
  }

  return { opacity: 0, scale: 0.96 };
}

export function getHeroCarouselWrapperStyle({
  isMobile,
}: ViewportStyleInput): CSSProperties {
  return {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    height: isMobile ? 302 : 352,
  };
}

export function getHeroControlsAnimation({
  isControlsVisible,
}: HeroControlsAnimationInput): TargetAndTransition {
  if (isControlsVisible) {
    return { opacity: 1, y: 0 };
  }

  return { opacity: 0, y: 10 };
}

export function getHeroControlsWrapperStyle({
  isMobile,
  isControlsVisible,
}: HeroControlsWrapperStyleInput): CSSProperties {
  return {
    willChange: 'opacity, transform',
    minHeight: isMobile ? 128 : 142,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: isControlsVisible ? 'auto' : 'none',
  };
}
